import { Bell, Printer, Search, User, Pin, Lightbulb, Activity, Moon, Shield, Share2, Check, Siren, UserCircle2, Flashlight, Sun, Box } from 'lucide-react';
import { R2NotificationCenter } from '../notifications/R2NotificationCenter.js';
import { useUXControlStore } from '../../stores/uxControl.store.js';
import { useThemeStore } from '../../stores/theme.store.js';
import { useEffect, useState } from 'react';
import type { Route } from '../../App.js';
import { HealthWidget } from './HealthWidget.js';
import { HelpMenu } from '../help/HelpMenu.js';
import { CalibrationIndicator } from '../calibration/CalibrationIndicator.js';
import { pfeStore } from '../../stores/pfe.store.js';
import { launcherStore } from '../../stores/launcher.store.js';
import { ingestStore } from '../../stores/ingest.store.js';
import { CyberpunkLauncherButton } from '../launcher/CyberpunkLauncher.js';
import { useWorkspacePrefs } from '../../hooks/useWorkspacePrefs.js';
import { useExperienceMode } from '../../stores/experienceMode.store.js';
import { call } from '../../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { ConnectivityBadge } from '../branding/ConnectivityBadge.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { FrisIndicator } from '../fris/FrisIndicator.js';
import { useLicense } from '../../hooks/useLicense.js';
import { StealthToggle } from '../stealth/StealthToggle.js';
import { RcddBadge } from '../branding/RcddBadge.js';
import { haptic } from '../../lib/soundManager.js';
import { useEliteStore } from '../../stores/elite.store.js';
import { useRedAlertStore, redAlertStore } from '../../stores/redAlert.store.js';
import { useAvatarStore } from '../../stores/avatar.store.js';
import { useNightOpsStore } from '../../stores/nightOps.store.js';
import { NightOpsPanel } from '../nightOps/NightOpsPanel.js';
import { useNightLightStore } from '../../stores/nightLight.store.js';
import { NightLightControls } from '../ux/NightLightControls.js';
import { useFloatingWindowStore } from '../../stores/floatingWindow.store.js';

// Print is only surfaced where a printed page has real value (drawings,
// support, pricing, sync, intelligence hubs, etc.) — and never rendered on the
// public login/auth screens, which mount without the TopBar entirely.
const PRINT_VALUE_ROUTES: ReadonlySet<Route> = new Set<Route>([
  'dashboard', 'documents', 'doc-control', 'auto-report', 'projects',
  'racks', 'cabling', 'assets', 'buildings', 'certification-pass',
  'estimator', 'civil', 'labor-timeline', 'schedule', 'clashes',
]);

const TITLES: Partial<Record<Route, string>> & Record<string, string> = {
  dashboard: 'Operations Dashboard',
  assets: 'Asset Registry',
  buildings: 'Building Hierarchy',
  racks: 'Rack & Telecom Spaces',
  cabling: 'Cabling Workspace',
  projects: 'Projects',
  clashes: 'Clash Detection',
  documents: 'Documents & Drawings',
  sync: 'Synchronization Engine',
  ai: 'Emmy — Engineering Advisor',
  'coach-validation': 'Coach Validation — P1-01',
  comms: 'Communications — B4 Suggest-mode Email',
  autopilot: 'Project Autopilot — B5 (plan-only)',
  agent: 'AI Agent Projects',
  cost: 'Company Cost Templates — B8',
  backup: 'Automated Backup & DR — B9',
  health: 'Health Monitoring — P1.4',
  field:  'Field Operations — B-FIELD.1',
  propagation: 'Change Propagation — GCPE.1',
  'doc-control': 'Document Control Engine',
  settings: 'Settings',
  creator: 'About the Creator',
  schedule: 'Schedule — Calendar Engine',
  sie: 'Smart Ingestion Engine — SIE',
  'auto-report': 'Project Health & Activity Report',
  pricing:          'License & Subscription',
  'global-command': 'Global Command Centre',
  'help':           'Support & Feedback',
  'parts-library':  'Parts Library',
  'monitor':        'Monitor Dashboard',
  'critical-infra': 'Critical Infrastructure Intelligence',
  'intelligence': 'Industry Intelligence & Ads',
  'estimator': 'RCD² Estimator Engine',
  'civil': 'Civil Ductbank & Manhole Estimator',
  'labor-timeline': 'Labor & Timeline Agent',
  'certification-pass': 'Project Certification Pass',
  'capabilities': 'R2 Capabilities Overview',
  'r2-brain': 'R2 Brain — Intelligence Command Centre',
  'r2-academy': 'R2 Academy — Training Assistant',
  'r2-news': 'R2 News Feed',
  'r2-voice': 'R2 Voice — Conversational Interface',
  'rail-engine': 'R2 Rail Infrastructure Package Engine',
  'marine-cable': 'R2 Marine Cable Generator · Subsea & Underground Engine',
  'community-builder': 'R2 Community Builder · Community Development Engine',
  'bunker-builder': 'R2 Resilience Bunker Builder · Concept Lab',
  'founder-approvals': 'Founder Approvals Inbox',
  'quote-generator': 'R2 Quote Generator',
  'venue-studio': 'R2 Venue & Amenity Studio · Design · Visualize · Experience',
  'manufacturing': 'R2 Manufacturing Generator · Industrial Intelligence Engine',
  'frequency-lab': 'R2 Frequency Lab · Frequency & Sound Engine',
  'integration-bridge': 'R2 Integration Bridge · Interoperability Engine',
  'site-audit': 'R2 Site Audit · Mobile Field Workflow',
  'markup-checkout': 'R2 Markup & Checkout · QA/QC + Returning Discount',
  'vending-machine': 'R2 Engineering Vending Machine · Concierge',
  'hospital-generator': 'R2 Hospital Generator · Healthcare Engine',
  'viie': 'R2 Vertical Infrastructure Intelligence Engine (VIIE)',
  'reality-translator': 'Reality Intelligence Engine · Physical → Digital Twin',
  'r2-equipment-library': 'Equipment Library · R2 Master Database · 25+ manufacturers · BIM/CAD · Lifecycle',
  'finance': 'R2 Personal Finance · Financial Intelligence Platform',
  'finance-pro': 'R2 Business, Corporate & International Finance · FIP Phases 2-4',
  'underground-corridor': 'R2 Underground Utility Corridor · Subsea & Underground Engine',
  'carbon-intelligence': 'R2 Carbon Intelligence Engine · Embodied + Operational CO₂e',
  'business-creator': 'R2 Business Creator · Vision Builder',
  'marketing-controls': 'Marketing & Display Controls',
  'managed-services': 'Managed CAD/BIM Service · R2 Coordination',
  'mie': 'Multimedia Intelligence Engine · MIE 2.0',
  'job-queue': 'R2 Background Job Queue · Render & Media Library',
  'feature-flags': 'Feature Flag Manager · Visibility Controls',
  'global-map': 'Global Map · Geographic Intelligence',
  'bim-project-config': 'BIM Project Configuration · Service Wizard',
  'bim-engine': 'BIM Generation Engine · AI Build Queue',
  'bim-operator-dashboard': 'BIM Operator Dashboard · Pipeline Management',
  'marketplace': '3D Model Marketplace · BIM Assets & Deliverables',
  'marketplace-orders': 'My BIM Orders · Delivery & Pipeline',
  'marketplace-operator': 'Marketplace Operations · Operator Dashboard',
  'investor-relations': 'Investor Relations · Allocation Transparency',
  'cie':              'R2 Daily Adventures · Creative Intelligence Engine',
  'automation-studio': 'R2 Automation Studio · Visual Workflow Builder',
  'r2-calendar':      'R2 Intelligent Project Calendar · Anticipation Intelligence',
  'r2-safety':        'R2 Safety Intelligence · Lone Worker & Emergency Response',
  'r2-financial-command': 'R2 Financial Command Centre · Live Markets & Investor Pipeline',
  'r2-timesheet':     'R2 Timesheet Automation · Smart Work Submission Engine',
  'r2-pie':           'R2 Personal Intelligence Engine · Living Profile',
  'r2-finance-hub':   'R2 Financial Intelligence Platform · FIP — 14 Modules',
  'r2-mission-control': 'R2 Mission Control · Platform Health & Service Registry',
  'r2-ai-coach':      'R2 AI Coach · Hybrid LLM Smart Routing',
  'profile':          'Profile & License · Account Management',
  'r2-finance':       'R2 Finance · Project Financial Intelligence',
  'voice':            'R2 Voice · Intelligence Communication',
  'index':            'Platform Directory · All RCD² Modules',
  'r2-3d-engine':            'R2 3D Intelligence Engine · Parametric · AI-Assisted · BIM',
  'r2-smart-viewer':         'R2 SmartViewer · Browser-Based 3D File Viewer',
  'r2-permissions':          'R2 Permissions · Role-Based Access Control',
  'r2-measurement-engine':   'R2 Smart Measurement Engine · Precision · Cross-Page Memory',
  'r2-wsie':                 'Workspace Sticky Intelligence Engine · Notes · Collaboration',
  'r2-permit-intelligence':  'Permit Intelligence Engine (PIE) · AI-Powered Permit Analysis',
  'r2-core-map':             'R2 Core Ecosystem Map · Founder Mode',
  'r2-marketplace':          'R2 Marketplace · Self-Service Engineering Services',
  'r2-vending-machine':      'R2 Vending Machine™ · Order Drawings & AI Engineering',
  'r2-drawing-generator':    'R2 AI Drawing Generator · Upload · Analyse · Order · Deliver',
  'r2-gis-intelligence':     'R2 GIS / Civil Intelligence · KML · OSP Telecom Design',
  'r2-intelligent-building': 'R2 100-Storey Intelligent Building · Digital Twin · Live Analytics',
  'r2-bins-explorer':        'BINS Explorer · Building Information Navigation System · Campus → Port hierarchy · AI Summaries',
  'r2-delivery-portal':      'R2 Order Delivery Portal · Track · Download · Revisions',
  'r2-customer-dashboard':   'My Dashboard · Orders · AI Jobs · Delivery · Billing',
};

// ── DisplayModeFlyout — collapses 5 visual toggles into one clean pill ────────
function DisplayModeFlyout({
  prefs, setPref, r2Mode, toggleWave, toggleDim, activateAnxiety, deactivateMode,
}: {
  prefs: ReturnType<typeof useWorkspacePrefs>['prefs'];
  setPref: ReturnType<typeof useWorkspacePrefs>['setPref'];
  r2Mode: string;
  toggleWave: () => void;
  toggleDim: () => void;
  activateAnxiety: () => void;
  deactivateMode: () => void;
}) {
  const [open, setOpen] = useState(false);

  const activeMode =
    prefs.neonMode    ? { label: 'Neon',    color: 'rgba(236,72,153,0.8)' } :
    prefs.waveMode    ? { label: 'Warp',    color: 'rgba(37,181,168,0.8)' } :
    r2Mode === 'dim'  ? { label: 'Dim',     color: 'rgba(129,140,248,0.8)' } :
    r2Mode === 'anxiety' ? { label: 'Calm', color: 'rgba(52,211,153,0.8)' } :
    null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        data-testid="display-mode-flyout-btn"
        onClick={() => setOpen((v) => !v)}
        className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
        style={activeMode ? {
          border: `1px solid ${activeMode.color}`,
          background: `${activeMode.color.replace('0.8', '0.1')}`,
          color: activeMode.color,
        } : {
          border: '1px solid rgba(71,85,105,0.6)',
          background: 'transparent',
          color: 'rgba(148,163,184,0.9)',
        }}
      >
        <Lightbulb className="h-3 w-3" strokeWidth={2} />
        <span>{activeMode ? activeMode.label : 'Display'}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ opacity: 0.6 }}>
          <path d="M1 2.5L4 5.5L7 2.5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          {/* Flyout */}
          <div
            data-testid="display-mode-flyout"
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', left: 0,
              width: 200, zIndex: 50,
              background: 'rgba(8,14,26,0.97)',
              border: '1px solid rgba(37,181,168,0.18)',
              borderRadius: 10, padding: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(20px)',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            {[
              { id: 'neon',    label: 'Neon',        icon: <Lightbulb className="h-3 w-3" />, active: prefs.neonMode,     toggle: () => { setPref('neonMode', !prefs.neonMode); setOpen(false); }, color: 'rgba(236,72,153,0.8)' },
              { id: 'wave',    label: 'Warp Drive',  icon: <Activity className="h-3 w-3" />,  active: prefs.waveMode,     toggle: () => { toggleWave(); setOpen(false); },  color: 'rgba(37,181,168,0.8)' },
              { id: 'dim',     label: 'Dim',         icon: <Moon className="h-3 w-3" />,      active: r2Mode === 'dim',   toggle: () => { toggleDim(); setOpen(false); },   color: 'rgba(129,140,248,0.8)' },
              { id: 'calm',    label: 'Calm',        icon: <Shield className="h-3 w-3" />,    active: r2Mode === 'anxiety', toggle: () => { r2Mode === 'anxiety' ? deactivateMode() : activateAnxiety(); setOpen(false); }, color: 'rgba(52,211,153,0.8)' },
              { id: 'stealth', label: 'Stealth',     icon: <Moon className="h-3 w-3" />,      active: false,              toggle: () => setOpen(false), color: 'rgba(100,116,139,0.8)' },
            ].map((m) => (
              <button
                key={m.id}
                data-testid={`display-mode-${m.id}`}
                onClick={m.toggle}
                className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[11px] w-full text-left transition-all"
                style={m.active ? {
                  background: `${m.color.replace('0.8', '0.1')}`,
                  border: `1px solid ${m.color.replace('0.8', '0.35')}`,
                  color: m.color,
                } : {
                  background: 'transparent', border: '1px solid transparent',
                  color: 'rgba(148,163,184,0.85)',
                }}
              >
                {m.icon}
                <span className="flex-1 font-medium">{m.label}</span>
                {m.active && <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.color, boxShadow: `0 0 5px ${m.color}` }} />}
              </button>
            ))}
            {/* Stealth is a standalone component — render inline */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)', marginTop: 2, paddingTop: 4 }}>
              <StealthToggle />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function TopBar({ route, onNavigate }: { route: Route; onNavigate?: (r: Route) => void }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const displayName = user?.email?.split('@')[0] ?? 'user';
  const { prefs, setPref } = useWorkspacePrefs();
  const r2Mode = useExperienceMode((s) => s.mode);
  const toggleDim = useExperienceMode((s) => s.toggleDim);
  const activateAnxiety = useExperienceMode((s) => s.activateAnxiety);
  const deactivateMode = useExperienceMode((s) => s.deactivate);
  const pinned = prefs.alwaysOnTop;
  const [inviteCopied, setInviteCopied] = useState(false);
  const copyInvite = (): void => {
    try { void navigator.clipboard?.writeText('https://rcd2engine.com/invite'); } catch { /* clipboard blocked */ }
    setInviteCopied(true);
    window.setTimeout(() => setInviteCopied(false), 1800);
  };
  const { licenseStatus } = useLicense();
  const eliteVerified = useEliteStore((s) => s.verified);
  const showRcdd =
    licenseStatus?.status === 'verified' &&
    (licenseStatus.tier === 'contractor' || licenseStatus.tier === 'designer') &&
    !!licenseStatus.bicsiId;

  // Red Alert Mode
  const redAlert = useRedAlertStore();
  const isRedAlert = redAlert.mode === 'red-alert';

  // Avatar Overlay
  const avatarOverlay = useAvatarStore();
  const isAvatarOn = avatarOverlay.isOverlayVisible;

  // Night Operations Mode
  const nightOps = useNightOpsStore();
  const isNightOps = nightOps.active;
  const [showNightPanel, setShowNightPanel] = useState(false);

  // Night Light Navigation
  const nightLight = useNightLightStore();
  const isNightLight = nightLight.enabled;
  const [showNightLightPanel, setShowNightLightPanel] = useState(false);

  const togglePin = () => {
    const next = !pinned;
    setPref('alwaysOnTop', next);
    void call<{ value: boolean }, { ok: boolean; value: boolean }>(
      IPC.SYSTEM_SET_ALWAYS_ON_TOP, { value: next },
    ).catch(() => { /* web preview — no main process */ });
  };

  // Neon Mode — toggle a root class that boosts glow/drop-shadow app-wide.
  useEffect(() => {
    document.documentElement.classList.toggle('neon-mode', prefs.neonMode);
  }, [prefs.neonMode]);

  // Focus Mode — dims decorative chrome & softens glows for deep work.
  useEffect(() => {
    document.documentElement.classList.toggle('focus-mode', prefs.focusMode);
  }, [prefs.focusMode]);

  // Workspace layout density — Marketing & Display Controls surface.
  useEffect(() => {
    document.documentElement.setAttribute('data-density', prefs.workspaceLayout);
  }, [prefs.workspaceLayout]);

  // Wave Mode → sustain the Global Warp Drive while active.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: prefs.waveMode } }));
  }, [prefs.waveMode]);

  // Identity Transformation → Gold Enterprise re-skin when RCDD verified.
  useEffect(() => {
    document.documentElement.classList.toggle('elite-mode', eliteVerified);
  }, [eliteVerified]);

  // Red Alert Mode — adds subtle amber tint to root for passive awareness.
  useEffect(() => {
    document.documentElement.classList.toggle('red-alert-mode', isRedAlert);
  }, [isRedAlert]);

  // Background Toggle — White (apple-white) ↔ Dark (engineering-dark)
  const { activeTheme, setTheme } = useThemeStore();
  const isLightMode = activeTheme === 'apple-white';
  const toggleBg = () => setTheme(isLightMode ? 'engineering-dark' : 'apple-white');

  // Wave toggle = fire the engines: warp + take-off shake + hum + haptic.
  const toggleWave = () => {
    const next = !prefs.waveMode;
    setPref('waveMode', next);
    setPref('backgroundEffectsEnabled', next); // ON/OFF controls the canvas too
    if (next) {
      haptic([12, 30, 60]);                 // iPhone-style launch tap
      const root = document.documentElement;
      root.classList.add('warp-takeoff');   // 1s tile shake
      window.setTimeout(() => root.classList.remove('warp-takeoff'), 1050);
    } else {
      haptic(8);
      window.dispatchEvent(new CustomEvent('r2:warp', { detail: { active: false } }));
    }
  };
  // Global keyboard surface — see CyberpunkLauncher for the visual entry
  // point. All system-level UI must register itself in launcherRegistry.ts.
  // Note: Ctrl+K is reserved for Universal Search (App.tsx) — do not re-bind here.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        e.preventDefault();
        toggleBg();
      }
      // Ctrl+K → Universal Search (handled in App.tsx). Ctrl+Shift+K → Cyberpunk Launcher
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        launcherStore.toggle();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        useFloatingWindowStore.getState().openWindow('smart-ingestion');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
        e.preventDefault();
        const manager = useFloatingWindowStore.getState();
        if (pfeStore.getState().open) manager.closeWindow('project-explorer');
        else manager.openWindow('project-explorer');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header
      data-testid="top-bar"
      className="h-14 shrink-0 flex items-center gap-3 md:gap-6 pl-16 pr-3 md:px-6 backdrop-blur-sm overflow-x-hidden"
      style={{
        background:          'rgba(8, 16, 36, 0.78)',
        backdropFilter:      'blur(20px)',
        WebkitBackdropFilter:'blur(20px)',
        borderBottom:        '1px solid rgba(37,181,168,0.13)',
        position:            'relative',
        zIndex:               10,
      }}
    >
      <div className="flex items-baseline gap-3 min-w-0 lg:min-w-[280px] shrink">
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em]" style={{ color: 'rgba(148,163,184,0.7)' }}>
          Module
        </span>
        <h1 className="text-sm font-semibold tracking-wide truncate" style={{ color: 'rgb(var(--fg))' }}>
          {TITLES[route]}
        </h1>
        <FrisIndicator featureId={route} />
      </div>

      {/* ── Mode Controls — Display flyout + Operational buttons ─────────── */}
      <div className="hidden md:flex items-center gap-2" data-testid="mode-controls">
        <DisplayModeFlyout
          prefs={prefs}
          setPref={setPref}
          r2Mode={r2Mode}
          toggleWave={toggleWave}
          toggleDim={toggleDim}
          activateAnxiety={activateAnxiety}
          deactivateMode={deactivateMode}
        />
        {/* Red Alert — operational, stays standalone */}
        <button
          data-testid="red-alert-toggle"
          onClick={() => redAlertStore.toggle('button')}
          aria-pressed={isRedAlert}
          title={isRedAlert ? 'Red Alert: ACTIVE — Click to return to normal.' : 'R2 Red Alert — calm, attentive support mode'}
          className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
          style={isRedAlert ? { border:'1px solid rgba(245,158,11,0.7)', background:'rgba(245,158,11,0.14)', color:'#fcd34d', boxShadow:'0 0 14px rgba(245,158,11,0.35)' }
            : { border:'1px solid rgba(71,85,105,0.6)', background:'transparent', color:'rgba(148,163,184,0.9)' }}
        >
          <Siren className="h-3 w-3" strokeWidth={2} style={{ filter: isRedAlert ? 'drop-shadow(0 0 5px rgba(245,158,11,0.9))' : 'none' }} />
          {isRedAlert && <span className="h-1.5 w-1.5 rounded-full" style={{ background:'#f59e0b', boxShadow:'0 0 6px rgba(245,158,11,0.9)', animation:'pulse 2s ease-in-out infinite' }} />}
          <span>{isRedAlert ? 'Alert' : 'R2 Alert'}</span>
        </button>
        {/* Avatar overlay — standalone */}
        <button
          data-testid="avatar-overlay-toggle-topbar"
          onClick={() => avatarOverlay.toggleOverlay()}
          aria-pressed={isAvatarOn}
          title={isAvatarOn ? 'Avatar: ACTIVE — Click to hide.' : 'Activate Avatar overlay'}
          className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
          style={isAvatarOn ? { border:'1px solid rgba(139,92,246,0.6)', background:'rgba(139,92,246,0.14)', color:'#c4b5fd', boxShadow:'0 0 12px rgba(139,92,246,0.4)' }
            : { border:'1px solid rgba(71,85,105,0.6)', background:'transparent', color:'rgba(148,163,184,0.9)' }}
        >
          <UserCircle2 className="h-3 w-3" strokeWidth={2} style={{ filter: isAvatarOn ? 'drop-shadow(0 0 4px rgba(139,92,246,0.9))' : 'none' }} />
          <span>{isAvatarOn ? 'Avatar' : 'Avatar'}</span>
        </button>
        {/* Night Ops — standalone */}
        <button
          data-testid="night-ops-topbar-btn"
          data-night-exempt
          onClick={() => { nightOps.toggle(); if (!isNightOps) setShowNightPanel(true); }}
          onContextMenu={(e) => { e.preventDefault(); setShowNightPanel(true); }}
          aria-pressed={isNightOps}
          title={isNightOps ? 'Night Ops: ACTIVE — right-click for settings' : 'Night Operations Mode'}
          className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
          style={isNightOps ? { border:'1px solid rgba(251,191,36,0.6)', background:'rgba(251,191,36,0.10)', color:'#FCD34D', boxShadow:'0 0 12px rgba(251,191,36,0.3)', animation:'flashlight-breathe 3s ease-in-out infinite' }
            : { border:'1px solid rgba(71,85,105,0.6)', background:'transparent', color:'rgba(148,163,184,0.9)' }}
        >
          <Flashlight className="h-3 w-3" strokeWidth={2} style={{ filter: isNightOps ? 'drop-shadow(0 0 4px rgba(251,191,36,0.9))' : 'none' }} />
          <span>{isNightOps ? 'Night' : 'Night'}</span>
        </button>
        {/* Night Light Navigation — Theater Mode */}
        <button
          data-testid="night-light-topbar-btn"
          onClick={() => setShowNightLightPanel((v) => !v)}
          aria-pressed={isNightLight}
          title="Night Light Navigation — Theater Mode (right-click for controls)"
          className="h-7 px-2 flex items-center gap-1.5 rounded-lg text-[9px] uppercase tracking-[0.14em] transition-all"
          style={isNightLight
            ? { border:'1px solid rgba(94,234,212,0.55)', background:'rgba(94,234,212,0.10)', color:'#5eead4', boxShadow:'0 0 10px rgba(94,234,212,0.25)' }
            : { border:'1px solid rgba(71,85,105,0.6)', background:'transparent', color:'rgba(148,163,184,0.9)' }}
        >
          <Moon className="h-3 w-3" strokeWidth={2} style={{ filter: isNightLight ? 'drop-shadow(0 0 4px rgba(94,234,212,0.9))' : 'none' }} />
          <span>Theater</span>
          {isNightLight && <span className="h-1.5 w-1.5 rounded-full" style={{ background:'#5eead4', boxShadow:'0 0 6px rgba(94,234,212,0.9)' }} />}
        </button>
      </div>

      {/* Night Ops settings panel */}
      {showNightPanel && <NightOpsPanel onClose={() => setShowNightPanel(false)} />}
      {/* Night Light controls */}
      {showNightLightPanel && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 240, zIndex: 200 }}>
          <NightLightControls onClose={() => setShowNightLightPanel(false)} />
        </div>
      )}

      <div className="hidden md:flex flex-1 items-center">
        <button
          data-testid="universal-search-btn"
          onClick={() => useUXControlStore.getState().openCommandPalette()}
          title="Universal Search — Ctrl+K or Shift+Shift"
          className="relative w-full max-w-[400px] h-8 rounded-xl flex items-center gap-2 px-3 text-left transition-all group"
          style={{
            background: 'rgba(10,20,45,0.7)',
            border: '1px solid rgba(37,181,168,0.2)',
            color: 'rgba(148,163,184,0.55)',
            cursor: 'text',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.border = '1px solid rgba(37,181,168,0.45)'; (e.currentTarget as HTMLElement).style.background = 'rgba(10,20,45,0.9)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.border = '1px solid rgba(37,181,168,0.2)'; (e.currentTarget as HTMLElement).style.background = 'rgba(10,20,45,0.7)'; }}
        >
          <Search className="h-3.5 w-3.5 shrink-0" style={{ color: 'rgba(37,181,168,0.5)' }} strokeWidth={1.75} />
          <span className="flex-1 text-[12px]">Search BIM objects, tasks, meetings…</span>
          <div className="flex items-center gap-1 shrink-0">
            <kbd style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(37,181,168,0.08)', border: '1px solid rgba(37,181,168,0.2)', color: 'rgba(94,234,212,0.5)', fontFamily: 'monospace', fontWeight: 700 }}>Ctrl+K</kbd>
          </div>
        </button>
      </div>

      <div className="flex items-center gap-2 lg:gap-3 shrink-0">
        <div className="hidden lg:flex items-center gap-3">
          <span className="pill" data-testid="sync-status-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Synced
          </span>
          <HelpMenu />
          <CalibrationIndicator />
          <CyberpunkLauncherButton onClick={() => launcherStore.toggle()} />
          {prefs.showInviteButtons && (
            <button
              data-testid="invite-btn"
              onClick={copyInvite}
              title="Copy a public invite link to share BIM OPS with colleagues & clients"
              className="h-8 px-3 flex items-center gap-2 rounded-xl text-[11px] uppercase tracking-[0.14em] transition"
              style={inviteCopied ? {
                border: '1px solid rgba(52,211,153,0.5)',
                background: 'rgba(52,211,153,0.14)',
                color: '#6ee7b7',
              } : {
                border: '1px solid rgba(245,158,11,0.4)',
                background: 'rgba(245,158,11,0.10)',
                color: '#fbbf24',
              }}
            >
              {inviteCopied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Share2 className="h-3.5 w-3.5" strokeWidth={1.75} />}
              <span>{inviteCopied ? 'Copied' : 'Invite'}</span>
            </button>
          )}
          {PRINT_VALUE_ROUTES.has(route) && (
            <button
              data-testid="print-btn"
              onClick={() => window.print()}
              title="Print or Save as PDF — share this view with clients"
              className="h-8 px-3 flex items-center gap-2 rounded-xl text-[11px] uppercase tracking-[0.14em] transition"
              style={{
                border: '1px solid rgba(20,184,166,0.35)',
                background: 'rgba(20,184,166,0.08)',
                color: 'rgb(var(--fg)/0.85)',
              }}
            >
              <Printer className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Print</span>
            </button>
          )}
          {/* Background Toggle — White ↔ Dark  (Cmd/Ctrl+Shift+L) */}
          <button
            data-testid="bg-toggle-btn"
            onClick={toggleBg}
            title={isLightMode ? 'Switch to Dark Mode (Cmd+Shift+L)' : 'Switch to Light Mode (Cmd+Shift+L)'}
            className="h-8 w-8 grid place-items-center rounded-xl transition-all"
            style={isLightMode ? {
              border: '1px solid rgba(251,191,36,0.55)',
              background: 'rgba(251,191,36,0.12)',
              color: '#fbbf24',
            } : {
              border: '1px solid rgba(71,85,105,0.5)',
              background: 'transparent',
              color: 'rgba(148,163,184,0.9)',
            }}
          >
            {isLightMode
              ? <Moon className="h-3.5 w-3.5" strokeWidth={1.75} />
              : <Sun  className="h-3.5 w-3.5" strokeWidth={1.75} />}
          </button>
          <R2NotificationCenter onNavigate={onNavigate} />
          {/* Health Widget — live 5-dot platform status */}
          <HealthWidget onOpenMC={onNavigate ? () => onNavigate('r2-mission-control') : undefined} />
          {/* Connectivity Mode badge — Live vs Secure Standalone */}
          <ConnectivityBadge />

          {/* RCDD Certified inline badge — contractor & designer tier, verified only */}
          {showRcdd && licenseStatus && (
            <div className="flex items-center gap-2">
              {eliteVerified && (
                <span
                  data-testid="bicsi-mark"
                  title="BICSI — Registered Communications Distribution Designer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', borderRadius: 6,
                    background: 'linear-gradient(135deg, rgba(252,211,77,0.16), rgba(37,181,168,0.12))',
                    border: '1px solid rgba(252,211,77,0.45)',
                    boxShadow: '0 0 10px rgba(252,211,77,0.18)',
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.14em', color: '#fde68a', textShadow: '0 0 6px rgba(252,211,77,0.5)' }}>BICSI</span>
                  <span style={{ width: 1, height: 10, background: 'rgba(252,211,77,0.35)' }} />
                  <span style={{ fontSize: 7, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(252,211,77,0.65)' }}>Registered</span>
                </span>
              )}
              <RcddBadge
                bicsiId={licenseStatus.bicsiId!}
                tier={licenseStatus.tier as 'contractor' | 'designer'}
                variant="inline"
              />
            </div>
          )}

          {/* Always-on-Top pin — standard window-control position, far right */}
          <button
            data-testid="always-on-top-pin-btn"
            onClick={togglePin}
            title={pinned ? 'Unpin window (restore normal z-order)' : 'Pin window — Always on top'}
            aria-pressed={pinned}
            className="h-8 w-8 grid place-items-center rounded-xl transition-all"
            style={pinned ? {
              border: '1px solid rgba(37,181,168,0.55)',
              background: 'rgba(37,181,168,0.15)',
              color: 'rgba(94,234,212,0.95)',
              boxShadow: '0 0 8px rgba(37,181,168,0.35)',
            } : {
              border: '1px solid rgba(71,85,105,0.5)',
              background: 'transparent',
              color: 'rgba(148,163,184,0.9)',
            }}
          >
            <Pin
              className="h-3.5 w-3.5 transition-transform"
              strokeWidth={pinned ? 2.25 : 1.75}
              style={{ transform: pinned ? 'rotate(-45deg)' : 'none' }}
            />
          </button>
        </div>

        {/* Always visible (incl. mobile) — user identity */}
        <button
          data-testid="user-menu-btn"
          className="h-8 px-2.5 flex items-center gap-2 rounded-xl text-[12px] transition shrink-0"
          style={{
            border: '1px solid rgba(71,85,105,0.6)',
            color: 'rgba(148,163,184,0.9)',
          }}
        >
          <User className="h-3.5 w-3.5" strokeWidth={1.75} />
          <span className="hidden sm:inline">{displayName}</span>
        </button>
      </div>
    </header>
  );
}
